import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocaleNavigate } from '@/lib/hooks/useLocaleNavigate';
import { Loader2, Lock } from 'lucide-react';
import { AppLayout } from '@/components/layout';
import { useAuth } from '@/lib/auth/auth-context';
import { AUTH_TOKEN_COOKIE, decodeJwtPayload, getCookie } from '@/lib/api/client';
import {
  payrollUnlockApiAuthPayrollUnlockPost,
  type PayrollUnlockRequestScreen,
} from '@/lib/api/generated';

/** HR-03-06 — the unlock lasts while Payroll screens are in use; after 15
 *  minutes without using one, the password is asked again. */
const IDLE_MS = 15 * 60 * 1000;
const STORAGE_KEY = 'yakson-payroll-unlock';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'] as const;

/** Identifies one sign-in (user + token issue time) without storing the token:
 *  a fresh sign-in gets a new value, so the lock is back (HR-03-06). */
function currentSession(): string | null {
  const token = getCookie(AUTH_TOKEN_COOKIE);
  if (!token) return null;
  const claims = decodeJwtPayload(token);
  if (!claims) return null;
  return `${String(claims.sub ?? '')}:${String(claims.iat ?? '')}`;
}

function readUnlocked(): boolean {
  const session = currentSession();
  if (!session) return false;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const saved = JSON.parse(raw) as { session?: string; lastActive?: number };
    return saved.session === session && Date.now() - (saved.lastActive ?? 0) < IDLE_MS;
  } catch {
    return false;
  }
}

function touch(): void {
  const session = currentSession();
  if (!session) return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ session, lastActive: Date.now() }));
  } catch {
    /* Private mode: the lock simply asks again on the next screen. */
  }
}

function forget(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

interface PayrollLockProps {
  screen: PayrollUnlockRequestScreen;
  children: ReactNode;
}

/**
 * HR-03 — Payroll asks for the password once more before it opens.
 *
 * A SECOND check on top of the existing access rules, which are unchanged:
 * the page behind still runs its own guards. While locked the page is not
 * rendered at all, so nothing behind the box is fetched, read or pressable.
 * The password is checked by the server (`POST /api/auth/payroll-unlock`),
 * which also writes the audit row; the fifth wrong try signs the user out.
 */
export function PayrollLock({ screen, children }: PayrollLockProps) {
  const { t } = useTranslation('salary');
  const { logout } = useAuth();
  const navigate = useLocaleNavigate();
  const [unlocked, setUnlocked] = useState(readUnlocked);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [checking, setChecking] = useState(false);

  // While unlocked: any use of the screen keeps it open; a minute timer
  // relocks it once 15 minutes pass without use.
  useEffect(() => {
    if (!unlocked) return;
    let last = 0;
    const onActivity = () => {
      const now = Date.now();
      if (now - last > 5000) {
        last = now;
        touch();
      }
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActivity, { passive: true }));
    const timer = window.setInterval(() => {
      if (!readUnlocked()) setUnlocked(false);
    }, 30_000);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActivity));
      window.clearInterval(timer);
    };
  }, [unlocked]);

  const submit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!password) {
        setError(t('payrollLock.enterPassword'));
        return;
      }
      setChecking(true);
      setError(null);
      try {
        const result = await payrollUnlockApiAuthPayrollUnlockPost({ password, screen });
        if (result.unlocked) {
          touch();
          setPassword('');
          setUnlocked(true);
          return;
        }
        if (result.signedOut) {
          forget();
          logout();
          navigate('/login', { replace: true });
          return;
        }
        setPassword('');
        setError(t('payrollLock.wrongPassword', { left: result.triesLeft }));
      } catch {
        setError(t('payrollLock.checkFailed'));
      } finally {
        setChecking(false);
      }
    },
    [password, screen, t, logout, navigate],
  );

  if (unlocked) return <>{children}</>;

  return (
    <AppLayout title={t('payrollLock.title')}>
      <div className="flex justify-center py-16">
        <form
          onSubmit={submit}
          className="w-full max-w-sm rounded-[18px] border border-line bg-white p-6 shadow-sm"
          data-testid="payroll-lock"
        >
          <div className="mb-4 flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-pink-soft">
              <Lock className="h-5 w-5 text-yakson-pink" />
            </div>
            <div>
              <h2 className="text-base font-semibold text-foreground">{t('payrollLock.title')}</h2>
              <p className="text-xs text-muted-foreground">{t('payrollLock.hint')}</p>
            </div>
          </div>
          <label htmlFor="payroll-lock-password" className="mb-1 block text-sm font-medium text-foreground">
            {t('payrollLock.password')}
          </label>
          <input
            id="payroll-lock-password"
            type="password"
            autoComplete="current-password"
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="payroll-lock-password"
            className="w-full rounded-lg border border-line bg-white px-3 py-2 text-sm focus:border-yakson-pink focus:outline-none focus:ring-1 focus:ring-yakson-pink"
          />
          {error && (
            <p className="mt-2 text-sm text-danger" role="alert" data-testid="payroll-lock-error">
              {error}
            </p>
          )}
          <div className="mt-5 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="rounded-lg border border-line px-4 py-2 text-sm font-medium text-foreground hover:bg-ground"
            >
              {t('payrollLock.cancel')}
            </button>
            <button
              type="submit"
              disabled={checking}
              data-testid="payroll-lock-submit"
              className="inline-flex items-center gap-2 rounded-lg bg-yakson-pink px-4 py-2 text-sm font-medium text-white hover:bg-yakson-pink-dark disabled:opacity-50"
            >
              {checking && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('payrollLock.open')}
            </button>
          </div>
        </form>
      </div>
    </AppLayout>
  );
}
